import CheckOutlined from '@ant-design/icons/CheckOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import App from 'antd/lib/app';
import Dropdown from 'antd/lib/dropdown';
import { MenuProps } from 'antd/lib/menu';
import Table from 'antd/lib/table';
import { ColumnsType } from 'antd/lib/table/interface';
import Typography from 'antd/lib/typography';
import { endOfMonth, startOfMonth } from 'date-fns';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOutletContext } from 'react-router';
import { MemoizedDatetimePopover } from '../../../components/datetimePopover/datetimePopover';
import { PageHeader } from '../../../components/PageHeader';
import { SearchInput } from '../../../components/searchInput/searchInput';
import { DatePeriodSelectorWithPresets } from '../../../components/selectors/DatePeriodSelectorWithPresets/DatePeriodSelectorWithPresets';
import { SelectedSiteFilterButton } from '../../../components/site/SelectedSiteFilterButton';
import { SiteAddress } from '../../../components/site/SiteAddress';
import { SiteMarketAndLoadZone } from '../../../components/site/SiteMarketAndLoadZone';
import { getColumnFilteredValue, getColumnSortOrder } from '../../../components/table/columnFormatHelpers';
import { WithEmptyDataTable } from '../../../components/table/withEmptyDataTable';
import { DEFAULT_PAGINATION } from '../../../domain/commonConst';
import { exportEvents } from '../../../domain/event';
import { IEvent, OptType } from '../../../domain/event/interface';
import { prepareEventReqDateParams } from '../../../domain/event/prepareEventReqDateParams';
import { useEventListQuery } from '../../../domain/event/queries';
import { IPaginationMeta } from '../../../domain/IPagination';
import { buildEventProgramTableFilter } from '../../../domain/program/buildEventProgramTableFilter';
import { usePrograms } from '../../../domain/program/usePrograms';
import { useMarketOptions } from '../../../domain/site/useMarketOptions';
import { UserType } from '../../../domain/user/interface';
import { IDateRange } from '../../interface';
import { toUsefulParams } from '../../toUsefulParams';
import { usePageLocation } from '../../usePageState';
import { EventsMetrics } from '../components/EventsMetrics/EventsMetrics';
import { EventResourceStatusCell } from '../components/EventResourceStatusCell/EventResourceStatusCell';
import { EventVenContext } from '../components/EventVenContext/EventVenContext';
import { useSiteUtilitiesListQuery } from 'src/domain/site/queries';
import { buildEventUtilityTableFilter } from 'src/domain/program/buildEventUtilityTableFilter';
import { getTablePaginationParams } from 'src/components/table/getTablePaginationParams';
import { useAuth } from 'src/domain/auth/useAuth';
import { useEventAccountManagersListQuery } from '../../../domain/filter-options/queries';
import { SelectedMarketAwardDispatchFilterButton } from 'src/components/site/SelectedMarketAwardDispatchFilterButton';
import { EventViewSwitcher } from '../components/EventViewSwitcher';
import '../Events.css';

const DEFAULT_PERIOD = {
    start: +startOfMonth(new Date()),
    end: +endOfMonth(new Date()),
};

const DEFAULT_SORTER = {
    field: 'event_datetime_start',
    order: 'descend',
};

export const EventHistoryView = () => {
    const { companyId } = useOutletContext<any>();

    const auth = useAuth()!;

    const location = useLocation();
    const { notification } = App.useApp();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: any = queryToState(location.search);
    const { programs } = usePrograms();

    const [exportLoading, setExportLoading] = useState<boolean>(false);
    const [date, setDate] = useState<IDateRange>({
        ...DEFAULT_PERIOD,
        ...(pageState?.end && pageState?.start && { start: pageState?.start, end: pageState?.end }),
    });

    const { data: utilitiesData } = useSiteUtilitiesListQuery();
    const utilities = utilitiesData ?? [];

    const eventsFilter = {
        ...pageState.filter,
        ...prepareEventReqDateParams(date),
        startInRange: true,

        search: pageState?.search,
        siteId: pageState?.siteId,
        companyId,
        marketAwardDispatchId: pageState?.marketAwardDispatchId,
    };

    const listQuery = {
        ...eventsFilter,

        pagination: pageState?.pagination ?? DEFAULT_PAGINATION,
        sorter: pageState?.sorter ?? DEFAULT_SORTER,
        include: 'site,company,program',
    };

    const { data, isFetching, isError, error } = useEventListQuery(listQuery, {
        refetchOnMount: 'always',
        keepPreviousData: true,
    });
    const events = data?.data ?? [];
    const meta = data?.meta ?? ({ total: 0 } as IPaginationMeta);

    if (isError) {
        notification.error({ key: 'fetch-events-data-error', message: error.message || 'Cannot fetch events data!' });
    }

    const { data: accountManagersData } = useEventAccountManagersListQuery(
        { company_id: listQuery.companyId },
        { enabled: auth.user?.isAdminRoleType() }
    );
    const accountManagers = accountManagersData || [];

    const handleExportAllEvents = async () => {
        setExportLoading(true);

        try {
            await exportEvents({
                ...pageState.filter,
                sorter: pageState.sorter || DEFAULT_SORTER,
                include: 'site,company',
                programId: pageState?.programId,
                companyId,
                marketAwardDispatchId: pageState?.marketAwardDispatchId,
                search: pageState?.search,
            });
        } catch (err: any) {
            notification.error({ key: 'events-export-error', message: err.message || 'Cannot export events!' });
        }

        setExportLoading(false);
    };

    const handleExportEvents = async () => {
        setExportLoading(true);

        try {
            await exportEvents({
                ...pageState.filter,
                sorter: pageState.sorter || DEFAULT_SORTER,
                include: 'site,company',
                programId: pageState?.programId,
                companyId,
                marketAwardDispatchId: pageState?.marketAwardDispatchId,

                ...prepareEventReqDateParams(date),
                startInRange: true,
                search: pageState?.search,
            });
        } catch (err: any) {
            notification.error({ key: 'events-export-error', message: err.message || 'Cannot export events!' });
        }

        setExportLoading(false);
    };

    const marketZoneFilterOptions = useMarketOptions({ includeOther: true });

    function onTableChange(pagination: any, filter: any, sorter: any) {
        const preparedParamsForQuery = toUsefulParams({ pagination, filter, sorter });

        setPageQuery({ ...pageState, ...preparedParamsForQuery });
    }

    const columns: ColumnsType<IEvent> = [
        {
            title: 'Start date',
            dataIndex: 'event_datetime_start',
            defaultSortOrder: 'descend',
            width: '120px',
            sorter: (a: any, b: any) => a.event_datetime_start - b.event_datetime_start,
            ...getColumnSortOrder('event_datetime_start', pageState.sorter),
            render: (text, record) => (
                <MemoizedDatetimePopover
                    timestamp={new Date(text).valueOf()}
                    timeZone={record.site?.site_timezone!}
                    siteName={record.site?.site_name}
                />
            ),
        },
        {
            title: 'Company',
            dataIndex: 'company_id',
            render: (text, record) => <div style={{ maxWidth: '200px' }}> {record?.site?.company?.company_name}</div>,
        },
        ...(auth.user?.isAdminRoleType()
            ? [
                  {
                      title: 'Account Manager',
                      dataIndex: 'account_manager',
                      width: '180px',
                      filters: accountManagers.map(accountManager => ({ text: accountManager.value, value: accountManager.value })),
                      ...getColumnFilteredValue('account_manager', pageState?.filter || {}),
                      filterSearch: (input: string, record: any) => !!record?.text?.toString()?.toLowerCase().includes(input.toLowerCase()),
                      render: (text: string, record: IEvent) => record?.account_manager,
                  },
              ]
            : []),
        {
            title: 'Site',
            dataIndex: 'site_id',
            render: (text, record) => <div style={{ maxWidth: '200px' }}>{record?.site?.site_name}</div>,
        },
        { ...buildEventProgramTableFilter(pageState, programs) },
        {
            title: 'VEN',
            width: '120px',
            render: (text, record) => <EventVenContext value={record.event_context?.customer_ven} />,
        },
        {
            title: 'Resource Status',
            width: '150px',
            align: 'center',
            filters: [
                { text: 'Online', value: 'online' },
                { text: 'Offline', value: 'offline' },
            ],
            dataIndex: 'resource_telemetry_status',
            ...getColumnFilteredValue('resource_telemetry_status', pageState.filter),
            filterMultiple: false,
            render: (_text, record) => <EventResourceStatusCell value={record.event_context?.resource_telemetry_status || null} />,
        },
        {
            title: 'Address',
            render: (text, record) => (
                <SiteAddress address={record.site?.site_address} city={record.site?.site_city} state={record.site?.site_state} />
            ),
        },
        { ...buildEventUtilityTableFilter(pageState, utilities) },
        {
            title: 'Market, Load Zone',
            dataIndex: ['market'],
            filterMultiple: false,
            width: 170,
            filters: marketZoneFilterOptions,
            ...getColumnFilteredValue('market', pageState.filter),
            render: (text, record) => (record.site ? <SiteMarketAndLoadZone site={record.site} /> : ''),
        },
        {
            title: 'Opt Type',
            dataIndex: 'event_opt_type',
            width: 100,
            filters: [
                { text: 'new', value: OptType.UNSET },
                { text: 'opt-out', value: OptType.OPT_OUT },
                { text: 'opt-in', value: OptType.OPT_IN },
            ],
            ...getColumnFilteredValue('event_opt_type', pageState.filter),
            filterMultiple: false,
            render: (text, record) => {
                if (record.event_opt_type === OptType.OPT_IN) return `opt-in`;
                if (record.event_opt_type === OptType.OPT_OUT) return `opt-out`;

                return '';
            },
        },
        {
            title: 'Duration',
            dataIndex: 'event_duration',
            render: text => <div data-cy="event-duration-cell"> {timeConvertor(text)}</div>,
        },
        {
            title: 'Canceled',
            align: 'center',
            dataIndex: 'event_cancelled',
            filters: [
                { text: 'Canceled', value: 'true' },
                { text: 'Not canceled', value: 'false' },
            ],
            ...getColumnFilteredValue('event_cancelled', pageState.filter),
            filterMultiple: false,
            render: text => text && <CheckOutlined data-cy="event-canceled" />,
        },
        {
            title: 'Completed',
            align: 'center',
            dataIndex: 'event_completed',
            filters: [
                { text: 'Completed', value: 'true' },
                { text: 'Not Completed', value: 'false' },
            ],
            ...getColumnFilteredValue('event_completed', pageState.filter),
            filterMultiple: false,
            render: (text, record) => record.event_completed && <CheckOutlined />,
        },
    ];

    const handleSearch = async (value: string) => {
        if (value === pageState.search) {
            setPageQuery({
                ...pageState,
                pagination: { ...pageState.pagination, current: DEFAULT_PAGINATION.current },
            });
            return;
        }

        setPageQuery({ ...pageState, search: value });
    };

    function onSiteFilterClear() {
        setPageQuery({ ...pageState, siteId: undefined });
    }

    function onMarketAwardDispatchFilterClear() {
        setPageQuery({ ...pageState, marketAwardDispatchId: undefined });
    }

    const onDateChange = (dateRange: IDateRange) => {
        setDate(dateRange);
        setPageQuery({
            ...pageState,
            start: dateRange.start.valueOf(),
            end: dateRange.end.valueOf(),
            ...toUsefulParams({ pagination: DEFAULT_PAGINATION }),
        });
    };

    const exportMenu: MenuProps = {
        items: [
            {
                key: 'export-all-events',
                label: 'Download All History',
                onClick: handleExportAllEvents,
            },
        ],
    };

    return (
        <>
            <PageHeader
                pageTitle="Events"
                extra={<EventViewSwitcher />}
                actions={[
                    !!pageState.siteId && (
                        <SelectedSiteFilterButton key="event-history-selected-site-filter" value={pageState.siteId} onClear={onSiteFilterClear} />
                    ),
                    !!pageState.marketAwardDispatchId && (
                        <SelectedMarketAwardDispatchFilterButton
                            key="event-history-selected-market-award-dispatch-filter"
                            value={pageState.marketAwardDispatchId}
                            onClear={onMarketAwardDispatchFilterClear}
                        />
                    ),
                    <Typography.Text key="event-history-date-period-label" strong>
                        Select Time Period:
                    </Typography.Text>,
                    <DatePeriodSelectorWithPresets
                        key="event-history-date-period-selector"
                        selectedPeriod={date}
                        onChange={onDateChange}
                        memoized={true}
                        disabled={exportLoading}
                    />,
                    <SearchInput key="event-history-search" onSearch={handleSearch} defaultValue={pageState?.search} />,
                    <Dropdown.Button
                        key="event-history-export"
                        data-cy="download-data"
                        onClick={handleExportEvents}
                        loading={exportLoading}
                        size="large"
                        menu={exportMenu}
                    >
                        {!exportLoading && <DownloadOutlined />} Download CSV
                    </Dropdown.Button>,
                ]}
            />

            {[UserType.CONTROL_PROVIDER, UserType.UTILITY_CUSTOMER].includes(auth.user?.user_type as UserType) === false && (
                <EventsMetrics filter={eventsFilter} key="event-metrics" />
            )}

            <Typography.Text style={{ display: 'block', color: 'darkgrey', marginBottom: 12, textAlign: 'center' }}>
                <InfoCircleOutlined /> The start date is displayed in your computer's time zone. Hover over the date to compare it with the site's
                local time zone and UTC
            </Typography.Text>

            <WithEmptyDataTable condition={isFetching} emptyDataDescription="No Data for Specified Search">
                <Table
                    size="small"
                    sticky={{ offsetHeader: 62 }}
                    rowKey="event_id"
                    columns={columns}
                    dataSource={events}
                    pagination={getTablePaginationParams(meta)}
                    loading={isFetching}
                    onChange={onTableChange}
                    className="event-history-table"
                />
            </WithEmptyDataTable>
        </>
    );
};

export function timeConvertor(duration: number) {
    if (!duration) {
        return '0m';
    }

    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    const minutesToFixed = Number.isInteger(minutes) ? minutes % 60 : Number(minutes.toFixed(2));

    const hourWithMinutes = minutesToFixed ? `${hours}h ${minutesToFixed}m` : `${hours}h`;

    return hours ? hourWithMinutes : `${minutesToFixed}m`;
}
